import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CarIcon, BikeIcon, TruckIcon, AlertTriangleIcon, ChevronLeftIcon, ChevronRightIcon, CheckCircleIcon, PhoneIcon, BoxIcon } from 'lucide-react';
interface FormData {
  accidentType: string;
  injured: string;
  medicalTreatment: string;
  accidentDate: string;
  atFault: string;
  hasLawyer: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
}
const initialFormData: FormData = {
  accidentType: '',
  injured: '',
  medicalTreatment: '',
  accidentDate: '',
  atFault: '',
  hasLawyer: '',
  firstName: '',
  lastName: '',
  phone: '',
  email: ''
};
const TOTAL_STEPS = 7;
export function MultiStepForm() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [disqualificationReason, setDisqualificationReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const goNext = () => {
    setDirection(1);
    setCurrentStep(prev => prev + 1);
  };
  const goBack = () => {
    setDirection(-1);
    setCurrentStep(prev => prev - 1);
  };
  const goToStart = () => {
    setDirection(-1);
    setCurrentStep(1);
    setIsDisqualified(false);
    setDisqualificationReason('');
    setFormData(initialFormData);
  };
  const handleSelect = (field: keyof FormData, value: string, autoAdvance = true) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Conditional Logic Checks
    if (field === 'injured' && value === 'No') {
      setIsDisqualified(true);
      setDisqualificationReason('injury');
      return;
    }
    if (field === 'accidentDate' && value === 'Más de 6 meses') {
      setIsDisqualified(true);
      setDisqualificationReason('time');
      return;
    }
    if (field === 'atFault' && value === 'Sí') {
      setIsDisqualified(true);
      setDisqualificationReason('fault');
      return;
    }
    if (field === 'hasLawyer' && value === 'Sí') {
      setIsDisqualified(true);
      setDisqualificationReason('lawyer');
      return;
    }
    if (autoAdvance) {
      setTimeout(() => {
        goNext();
      }, 300);
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const params = new URLSearchParams();
      params.append('firstName', formData.firstName);
      params.append('lastName', formData.lastName);
      params.append('phone', formData.phone);
      params.append('email', formData.email);
      params.append('accidentType', formData.accidentType);
      params.append('injured', formData.injured);
      params.append('medicalTreatment', formData.medicalTreatment);
      params.append('accidentDate', formData.accidentDate);
      params.append('atFault', formData.atFault);
      params.append('hasLawyer', formData.hasLawyer);
      await fetch('https://services.leadconnectorhq.com/hooks/3hVnR8h224OAFaOU1YLc/webhook-trigger/52f70fa2-cdda-4f7c-977c-dd5fb54e8a6b', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: params.toString(),
        mode: 'no-cors'
      });
    } catch (error) {
      console.error('Webhook error:', error);
    }
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'Lead', {
        content_name: formData.accidentType
      });
    }
    setIsSubmitting(false);
    navigate('/thank-you', {
      state: {
        fromSubmission: true
      }
    });
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };
  const renderDisqualification = () => {
    let message = '';
    if (disqualificationReason === 'fault') {
      message = 'Lo sentimos, en este momento no podemos ayudarle. Solo representamos a personas que no tuvieron la culpa del accidente.';
    } else if (disqualificationReason === 'lawyer') {
      message = 'Parece que ya tiene representación legal. Le deseamos éxito con su caso.';
    } else if (disqualificationReason === 'injury') {
      message = 'Lo sentimos, solo podemos representar casos con lesiones. Nos alegra que esté bien.';
    } else if (disqualificationReason === 'time') {
      message = 'Lo sentimos, el plazo para presentar una reclamación puede haber expirado.';
    }
    return <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="bg-orange-light border border-orange/30 rounded-2xl p-8 text-center max-w-2xl mx-auto shadow-xl" data-id="element-49">
        <AlertTriangleIcon className="w-16 h-16 text-orange mx-auto mb-6" data-id="element-50" />
        <h3 className="text-2xl font-bold text-navy mb-4" data-id="element-51">Aviso Importante</h3>
        <p className="text-lg text-gray-700 mb-8" data-id="element-52">{message}</p>

        <button onClick={goToStart} className="inline-flex items-center justify-center gap-2 bg-orange hover:bg-orange-hover text-white font-bold py-4 px-8 rounded-xl w-full md:w-auto transition-colors text-lg" data-id="element-53">
          Volver al inicio
        </button>
      </motion.div>;
  };
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <div className="space-y-3" data-id="element-54">
            <div className="text-center mb-4" data-id="element-55">
              <p className="text-sm text-gray-500 mb-1" data-id="element-56">
                Solo toma 2 minutos. Seleccione una opción para comenzar.
              </p>
              <h2 className="text-xl md:text-2xl font-bold text-navy" data-id="element-57">
                ELIJA EL TIPO DE ACCIDENTE:
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-id="element-58">
              {[{
              id: 'auto',
              icon: CarIcon,
              label: 'Accidente de Auto',
              desc: 'Colisión, trasero, múltiple vehículo'
            }, {
              id: 'moto',
              icon: BikeIcon,
              label: 'Accidente de Moto',
              desc: 'Motocicleta, scooter, bicicleta'
            }, {
              id: 'camion',
              icon: TruckIcon,
              label: 'Accidente de Camión/18 Ruedas',
              desc: 'Camión comercial, tráiler'
            }, {
              id: 'uber',
              icon: CarIcon,
              label: 'Uber/Lyft/Rideshare',
              desc: 'Como pasajero o conductor'
            }, {
              id: 'resbalon',
              icon: AlertTriangleIcon,
              label: 'Resbalón y Caída',
              desc: 'Tienda, trabajo, propiedad'
            }, {
              id: 'otro',
              icon: BoxIcon,
              label: 'Otro',
              desc: 'Cualquier otro tipo de accidente'
            }].map(item => {
              const isSelected = formData.accidentType === item.label;
              return <button key={item.id} onClick={() => handleSelect('accidentType', item.label)} className={`flex flex-col items-center justify-center text-center p-2.5 rounded-xl border-2 transition-all duration-200 ${isSelected ? 'border-orange bg-orange/5 text-orange' : 'border-gray-200 hover:border-orange text-gray-600 hover:text-orange bg-white'}`} data-id="element-59">
                    <item.icon className={`w-8 h-8 mb-2 ${isSelected ? 'text-orange' : 'text-gray-400'}`} data-id="element-60" />
                    <span className="text-sm font-bold leading-tight mb-1" data-id="element-61">
                      {item.label}
                    </span>
                    <span className="text-xs text-gray-500 leading-tight" data-id="element-62">
                      {item.desc}
                    </span>
                  </button>;
            })}
            </div>
          </div>;
      case 2:
        return <div className="space-y-3" data-id="element-63">
            <div className="text-center mb-4" data-id="element-64">
              <h2 className="text-xl md:text-2xl font-bold text-navy mb-1" data-id="element-65">
                ¿Resultó herido en el accidente?
              </h2>
              <p className="text-gray-500 font-medium text-sm" data-id="element-66">
                (Si no está seguro, seleccione Sí.)
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-lg mx-auto" data-id="element-67">
              {['Sí', 'No'].map(option => <button key={option} onClick={() => handleSelect('injured', option)} className={`py-4 px-6 rounded-xl border-2 text-lg font-bold transition-all duration-200 ${formData.injured === option ? 'border-orange bg-orange/5 text-orange' : 'border-gray-200 hover:border-orange text-gray-700 bg-white'}`} data-id="element-68">
                  {option}
                </button>)}
            </div>
          </div>;
      case 3:
        return <div className="space-y-3" data-id="element-69">
            <h2 className="text-xl md:text-2xl font-bold text-navy text-center mb-4" data-id="element-70">
              ¿Recibió tratamiento médico?
            </h2>
            <div className="grid grid-cols-1 gap-3 max-w-lg mx-auto" data-id="element-71">
              {['Sí', 'No', 'Todavía no, pero lo necesito'].map(option => <button key={option} onClick={() => handleSelect('medicalTreatment', option)} className={`py-4 px-6 rounded-xl border-2 text-lg font-bold transition-all duration-200 ${formData.medicalTreatment === option ? 'border-orange bg-orange/5 text-orange' : 'border-gray-200 hover:border-orange text-gray-700 bg-white'}`} data-id="element-72">
                  {option}
                </button>)}
            </div>
          </div>;
      case 4:
        return <div className="space-y-3" data-id="element-73">
            <h2 className="text-xl md:text-2xl font-bold text-navy text-center mb-4" data-id="element-74">
              ¿Cuándo ocurrió el accidente?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-lg mx-auto" data-id="element-75">
              {['Menos de 7 días', 'Menos de 1 mes', '1 a 6 meses', 'Más de 6 meses'].map(option => <button key={option} onClick={() => handleSelect('accidentDate', option)} className={`py-3 px-4 rounded-xl border-2 text-base font-bold transition-all duration-200 ${formData.accidentDate === option ? 'border-orange bg-orange/5 text-orange' : 'border-gray-200 hover:border-orange text-gray-700 bg-white'}`} data-id="element-76">
                  {option}
                </button>)}
            </div>
          </div>;
      case 5:
        return <div className="space-y-3" data-id="element-77">
            <h2 className="text-xl md:text-2xl font-bold text-navy text-center mb-4" data-id="element-78">
              ¿Fue usted el culpable del accidente?
            </h2>
            <div className="grid grid-cols-1 gap-3 max-w-lg mx-auto" data-id="element-79">
              {['Sí', 'No', 'No estoy seguro'].map(option => <button key={option} onClick={() => handleSelect('atFault', option)} className={`py-4 px-6 rounded-xl border-2 text-lg font-bold transition-all duration-200 ${formData.atFault === option ? 'border-orange bg-orange/5 text-orange' : 'border-gray-200 hover:border-orange text-gray-700 bg-white'}`} data-id="element-80">
                  {option}
                </button>)}
            </div>
          </div>;
      case 6:
        return <div className="space-y-3" data-id="element-81">
            <div className="text-center mb-4" data-id="element-82">
              <h2 className="text-xl md:text-2xl font-bold text-navy mb-1" data-id="element-83">
                ¿Ya tiene un abogado para su caso?
              </h2>
              <p className="text-gray-500 font-medium text-sm" data-id="element-84">
                (Si no tiene contrato firmado, seleccione No.)
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-lg mx-auto" data-id="element-85">
              {['Sí', 'No'].map(option => <button key={option} onClick={() => handleSelect('hasLawyer', option)} className={`py-4 px-6 rounded-xl border-2 text-lg font-bold transition-all duration-200 ${formData.hasLawyer === option ? 'border-orange bg-orange/5 text-orange' : 'border-gray-200 hover:border-orange text-gray-700 bg-white'}`} data-id="element-86">
                  {option}
                </button>)}
            </div>
          </div>;
      case 7:
        return <form onSubmit={handleSubmit} className="space-y-6" data-id="element-87">
            <div className="text-center mb-8" data-id="element-88">
              <h2 className="text-2xl md:text-3xl font-bold text-navy mb-2" data-id="element-89">
                ¡Último paso!
              </h2>
              <p className="text-gray-600 font-medium" data-id="element-90">
                ¿Cómo nos ponemos en contacto con usted?
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-91">
              <div data-id="element-92">
                <label className="block text-sm font-semibold text-gray-700 mb-1" data-id="element-93">
                  Nombre
                </label>
                <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} required className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-orange focus:ring-0 outline-none transition-colors" data-id="element-94" />
              </div>
              <div data-id="element-95">
                <label className="block text-sm font-semibold text-gray-700 mb-1" data-id="element-96">
                  Apellido
                </label>
                <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} required className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-orange focus:ring-0 outline-none transition-colors" data-id="element-97" />
              </div>
              <div data-id="element-98">
                <label className="block text-sm font-semibold text-gray-700 mb-1" data-id="element-99">
                  Teléfono
                </label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-orange focus:ring-0 outline-none transition-colors" data-id="element-100" />
              </div>
              <div data-id="element-101">
                <label className="block text-sm font-semibold text-gray-700 mb-1" data-id="element-102">
                  Correo electrónico
                </label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} required className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-orange focus:ring-0 outline-none transition-colors" data-id="element-103" />
              </div>
            </div>

            <button type="submit" className="w-full bg-orange hover:bg-orange-hover text-white font-black py-4 rounded-xl text-lg md:text-xl flex items-center justify-center gap-2 transition-all transform hover:scale-[1.02] shadow-lg" data-id="element-104">
              {isSubmitting ? 'ENVIANDO...' : 'ENVIAR MI CASO GRATIS'}{' '}
              {!isSubmitting && <ChevronRightIcon className="w-6 h-6" data-id="element-105" />}
            </button>

            <p className="text-xs text-gray-500 text-center mt-4 px-4 leading-relaxed" data-id="element-106">
              Al hacer clic en Enviar, usted consiente expresamente recibir
              llamadas y mensajes de texto de Founders Law mediante tecnología
              automatizada. El consentimiento no es condición de servicio.
              Acepta nuestra Política de Privacidad y Términos.
            </p>
          </form>;
      default:
        return null;
    }
  };
  if (isDisqualified) {
    return <div className="px-4 pb-20 md:pb-12 -mt-8 relative z-10" data-id="element-107">
        {renderDisqualification()}
      </div>;
  }
  return <div className="px-4 pb-16 md:pb-16 -mt-2 relative z-10" data-id="element-108">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl mx-auto overflow-hidden" data-id="element-109">
        {/* Progress Header */}
        {currentStep <= TOTAL_STEPS && <div className="bg-gray-50 border-b border-gray-100 px-4 py-2.5 md:px-6 md:py-3 flex flex-col items-center" data-id="element-110">
            <span className="text-sm font-black text-navy tracking-widest mb-1.5" data-id="element-111">
              PASO {currentStep} DE {TOTAL_STEPS}
            </span>
            <div className="flex gap-1 md:gap-1.5" data-id="element-112">
              {[...Array(TOTAL_STEPS)].map((_, i) => <div key={i} className={`h-2 rounded-full transition-all duration-300 ${i + 1 <= currentStep ? 'w-6 md:w-8 bg-orange' : 'w-2 md:w-3 bg-gray-200'}`} data-id="element-113" />)}
            </div>
          </div>}

        {/* Form Content Area */}
        <div className="p-3 md:p-5 relative min-h-[240px] flex flex-col" data-id="element-114">
          {currentStep > 1 && <button onClick={goBack} className="absolute top-4 left-4 text-gray-400 hover:text-orange flex items-center gap-1 font-semibold text-xs transition-colors z-20" data-id="element-115">
              <ChevronLeftIcon className="w-3.5 h-3.5" data-id="element-116" /> Anterior
            </button>}

          <div className="flex-1 flex flex-col justify-center mt-5 md:mt-2 relative overflow-hidden" data-id="element-117">
            <AnimatePresence mode="wait" custom={direction} data-id="element-118">
              <motion.div key={currentStep} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              x: {
                type: 'spring',
                stiffness: 300,
                damping: 30
              },
              opacity: {
                duration: 0.2
              }
            }} className="w-full" data-id="element-119">
                {renderStepContent()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>;
}